export const ZH_CN_CONTENTFUL_ASSET_HOST_TO_BASE = {
  'images-ctf.baslerweb.com': 'https://images-ctf.baslerweb.cn',
  'videos-ctf.baslerweb.com': 'https://videos-ctf.baslerweb.cn',
  'assets-ctf.baslerweb.com': 'https://assets-ctf.baslerweb.cn'
} as const;

export type ContentfulAssetRewritePolicy = {
  hostToBase: Readonly<Record<string, string>>;
};

/**
 * Locale-driven asset rewrite policies.
 *
 * Add new locales here to support additional host/domain mappings in the future.
 */
export const CONTENTFUL_ASSET_REWRITE_POLICIES = {
  'zh-cn': {
    hostToBase: ZH_CN_CONTENTFUL_ASSET_HOST_TO_BASE
  }
} as const satisfies Record<string, ContentfulAssetRewritePolicy>;

type PolicyLocale = keyof typeof CONTENTFUL_ASSET_REWRITE_POLICIES;

type PolicyRuntime = {
  hostToBase: Record<string, string>;
  hosts: string[];
  urlRegex: RegExp;
};

const policyRuntimeCache = new WeakMap<
  ContentfulAssetRewritePolicy,
  PolicyRuntime
>();

function escapeForRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildPolicyRuntime(
  policy: ContentfulAssetRewritePolicy
): PolicyRuntime {
  const cached = policyRuntimeCache.get(policy);
  if (cached) {
    return cached;
  }

  const hostToBase = Object.fromEntries(
    Object.entries(policy.hostToBase).map(([host, base]) => [
      host.toLowerCase(),
      base
    ])
  );
  const hosts = Object.keys(hostToBase);
  const hostsRegexFragment = hosts.map(escapeForRegex).join('|');
  const urlRegex = new RegExp(
    `(?:https?:)?\\/\\/(?:${hostsRegexFragment})(\\/[^\\s"'<>]*)?`,
    'gi'
  );

  const runtime = { hostToBase, hosts, urlRegex };
  policyRuntimeCache.set(policy, runtime);
  return runtime;
}

function getPolicyForLocale(
  locale: string | undefined
): ContentfulAssetRewritePolicy | undefined {
  const normalizedLocale = locale?.toLowerCase();
  if (!normalizedLocale) {
    return undefined;
  }

  return CONTENTFUL_ASSET_REWRITE_POLICIES[normalizedLocale as PolicyLocale];
}

function stringMightContainRewritableAssetHost(
  s: string,
  policy: ContentfulAssetRewritePolicy
): boolean {
  const { hosts } = buildPolicyRuntime(policy);
  const normalized = s.toLowerCase();
  for (const frag of hosts) {
    if (normalized.includes(frag)) {
      return true;
    }
  }
  return false;
}

function payloadContainsRewritableAssetHost(
  value: unknown,
  policy: ContentfulAssetRewritePolicy,
  depth = 0
): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (depth > 80) {
    return true;
  }
  if (typeof value === 'string') {
    return stringMightContainRewritableAssetHost(value, policy);
  }
  if (Array.isArray(value)) {
    return value.some((item) =>
      payloadContainsRewritableAssetHost(item, policy, depth + 1)
    );
  }
  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    return Object.values(value).some((v) =>
      payloadContainsRewritableAssetHost(v, policy, depth + 1)
    );
  }
  return false;
}

/** Contentful `fields.file.url` values are often protocol-relative (`//...`). */
export function toAbsoluteContentfulAssetUrl(url: string): string {
  if (!url) {
    return url;
  }
  if (url.startsWith('//')) {
    return `https:${url}`;
  }
  if (url.startsWith('http')) {
    return url;
  }
  return `https://${url}`;
}

/**
 * Rewrites a single Contentful asset URL using the given rewrite policy.
 */
export function rewriteContentfulAssetUrlByPolicy(
  url: string,
  policy: ContentfulAssetRewritePolicy
): string {
  if (!url || typeof url !== 'string') {
    return url;
  }
  if (!stringMightContainRewritableAssetHost(url, policy)) {
    return url;
  }

  try {
    const { hostToBase } = buildPolicyRuntime(policy);
    const normalized = url.startsWith('//')
      ? `https:${url}`
      : url.startsWith('http')
        ? url
        : `https://${url}`;

    const parsed = new URL(normalized);
    const base = hostToBase[parsed.hostname.toLowerCase()];
    if (!base) {
      return url;
    }

    return `${base}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

export function rewriteContentfulAssetUrlForLocale(
  url: string,
  locale: string | undefined
): string {
  const policy = getPolicyForLocale(locale);
  if (!policy) {
    return url;
  }

  return rewriteContentfulAssetUrlByPolicy(url, policy);
}

const CONTENTFUL_IMAGE_CDN_ORIGIN_URL = '//images-ctf.baslerweb.com/';

/**
 * Origin for `<link rel="preconnect">` — follows the same locale policy as
 * {@link rewriteContentfulAssetUrlForLocale}.
 */
export function resolveContentfulImageCdnOriginForLocale(
  locale: string | undefined
): string {
  const rewritten = rewriteContentfulAssetUrlForLocale(
    CONTENTFUL_IMAGE_CDN_ORIGIN_URL,
    locale
  );

  return new URL(toAbsoluteContentfulAssetUrl(rewritten)).origin;
}

function rewriteContentfulUrlsInStringByPolicy(
  s: string,
  policy: ContentfulAssetRewritePolicy
): string {
  if (!stringMightContainRewritableAssetHost(s, policy)) {
    return s;
  }

  const { urlRegex } = buildPolicyRuntime(policy);
  return s.replace(urlRegex, (fullMatch) =>
    rewriteContentfulAssetUrlByPolicy(fullMatch, policy)
  );
}

/**
 * Returns an immutable copy of `payload` with Contentful asset URLs rewritten according to `policy`
 * in all string leaves (including embedded URLs in longer strings).
 */
export function mapContentfulCdnByPolicyDeep<T>(
  payload: T,
  policy: ContentfulAssetRewritePolicy
): T {
  if (typeof payload === 'string') {
    return rewriteContentfulUrlsInStringByPolicy(payload, policy) as T;
  }

  if (Array.isArray(payload)) {
    return payload.map((item) =>
      mapContentfulCdnByPolicyDeep(item, policy)
    ) as T;
  }

  if (payload !== null && typeof payload === 'object') {
    if (payload instanceof Date) {
      return payload;
    }

    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(payload)) {
      out[key] = mapContentfulCdnByPolicyDeep(value, policy);
    }
    return out as T;
  }

  return payload;
}

/**
 * Rewrites Contentful asset hosts using the locale policy.
 * Other locales keep the original payload reference.
 *
 * Skips traversal when there are no rewritable hosts
 * (important for server rewrite + `useFetch` `transform` combination).
 */
export function applyContentfulAssetCdnByLocale<T>(
  payload: T,
  locale: string | undefined
): T {
  const policy = getPolicyForLocale(locale);
  if (!policy) {
    return payload;
  }
  if (!payloadContainsRewritableAssetHost(payload, policy)) {
    return payload;
  }

  return mapContentfulCdnByPolicyDeep(payload, policy);
}
